import {
  computeGridTemplateColumns,
  usePagination,
  useSorting,
  DataTableColumn,
  SortDirection,
  CLIENT,
  SERVER,
  SCROLL_AMOUNT,
} from '@shared/ui/data-table';
import { PaginationFooter } from './pagination-footer';
import * as stylex from '@stylexjs/stylex';
import { Columns } from './columns';
import { styles } from './styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Rows } from './rows';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { ChevronLeftIcon } from '@shared/assets/icons';

interface DataTableProps<T extends object> {
  columns: DataTableColumn<T>[];
  rows: T[];
  pageSize?: number;
  pagination?: boolean;
  onPageChange?: (page: number) => void;
  rowCount?: number;
  page?: number;
  totalPage?: number;
  onSortChange?: (
    sortDirection: SortDirection,
    sortColumn: Extract<keyof T, string>,
    activeSorts?: { column: Extract<keyof T, string>; direction: Exclude<SortDirection, null> }[]
  ) => void;
  paginationMode?: typeof CLIENT | typeof SERVER;
  sortingMode?: typeof CLIENT | typeof SERVER;
  multiSort?: boolean;
  loading?: boolean;
  containerClassName?: string;
  hasMore?: boolean;
  loadMore?: () => void;
  scrollable?: boolean;
  tableClassName?: string;
  selectedRows?: Array<string | number>;
  selectedClassName?: string;
}

const getSkeletons = (pageSize: number = 10) =>
  new Array(pageSize).fill(0).map((_, index) => <div key={index} {...stylex.props(styles.skeletonLoading)} />);

const DataTable = <T extends object>({
  columns,
  rows,
  pageSize,
  pagination,
  onPageChange,
  rowCount,
  paginationMode = CLIENT,
  page,
  totalPage,
  onSortChange,
  loading,
  sortingMode = CLIENT,
  multiSort = false,
  containerClassName,
  hasMore,
  loadMore,
  scrollable,
  tableClassName,
  selectedRows,
  selectedClassName,
}: DataTableProps<T>) => {
  const { pageCount, handlePrevPage, handleNextPage } = usePagination({
    paginationMode,
    totalPage,
    rowCount,
    pageSize,
    page,
    onPageChange,
  });

  const { displayedRows, sortDisplayedRows } = useSorting(rows);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const sortClickHandler = (
    column: DataTableColumn<T>,
    newSortDirection: SortDirection,
    activeSorts?: { column: Extract<keyof T, string>; direction: Exclude<SortDirection, null> }[]
  ) => {
    if (sortingMode === SERVER) {
      onSortChange?.(newSortDirection, column.field, activeSorts);
      return;
    }

    sortDisplayedRows(newSortDirection, column.field);
  };

  const rowsToDisplay = useMemo(() => {
    if (paginationMode === SERVER) {
      return displayedRows;
    }

    if (!page || !pageSize) {
      return displayedRows;
    }

    if (page === 1) {
      return displayedRows.slice(0, pageSize);
    }

    return displayedRows.slice((page - 1) * pageSize, page * pageSize);
  }, [paginationMode, page, displayedRows, pageSize]);

  const columnsToDisplay = useMemo(() => columns.filter(({ hidden }) => !hidden), [columns]);
  const gridTemplateColumns = computeGridTemplateColumns(columnsToDisplay);

  const tableContainerClassName = [stylex.props(styles.container).className, containerClassName].join(' ');

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1); // -1 для погрешности
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollButtons();

    el.addEventListener('scroll', updateScrollButtons);

    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, [updateScrollButtons]);

  const handleScrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  return (
    <div className={tableContainerClassName}>
      <div {...stylex.props(styles.scrollArea)}>
        {scrollable && !loading && (
          <div {...stylex.props(styles.scrollButtons)}>
            {canScrollLeft && (
              <UnstyledButton {...stylex.props(styles.scrollButton)} onClick={handleScrollLeft}>
                <ChevronLeftIcon size={32} color="currentColor" />
              </UnstyledButton>
            )}

            {canScrollRight && (
              <UnstyledButton
                {...stylex.props(styles.scrollButton, styles.scrollButtonRight)}
                onClick={handleScrollRight}
              >
                <ChevronLeftIcon size={32} color="currentColor" />
              </UnstyledButton>
            )}
          </div>
        )}

        <div
          ref={scrollRef}
          className={tableClassName}
          style={{ minWidth: 'auto', overflow: loading ? 'hidden' : 'auto' }}
        >
          <Columns
            columns={columnsToDisplay}
            sortClickHandler={sortClickHandler}
            gridTemplateColumns={gridTemplateColumns}
            multiSort={multiSort}
          />

          <Rows
            rows={rowsToDisplay}
            columns={columnsToDisplay}
            gridTemplateColumns={gridTemplateColumns}
            hasMore={hasMore}
            loadMore={loadMore}
            isLoading={loading}
            selectedRows={selectedRows}
            selectedClassName={selectedClassName}
          />
        </div>
      </div>

      {loading && getSkeletons(pageSize)}

      <PaginationFooter
        pagination={!!pagination}
        page={page}
        onPageChange={onPageChange}
        pageCount={pageCount}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
      />
    </div>
  );
};

export { DataTable };
